import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import PreviewSite from './PreviewSite';
import './VoirSite.css';

// ══════════════════════════════════════════════════════════════
// PEFEP Site — PAGE VOIR UN SITE (depuis le tableau de bord)
// Route : /mes-sites/:id
// Query : ?scroll=paiement (pour scroll auto vers paiement)
// ══════════════════════════════════════════════════════════════

export default function VoirSite() {
  const { id: siteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [site, setSite] = useState(null);

  const containerRef = useRef(null);

  // ─────────────────────────────────────────────────────────────
  // Charger le site depuis Supabase
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const chargerSite = async () => {
      if (!siteId) {
        setErreur('Aucun identifiant de site fourni');
        setChargement(false);
        return;
      }

      if (!user?.id) {
        setErreur('Vous devez être connecté');
        setChargement(false);
        return;
      }

      try {
        // ⭐ Admin peut voir TOUS les sites, client normal uniquement les siens
        let query = supabase
          .from('sites_generes')
          .select('*')
          .eq('id', siteId);

        if (user?.role !== 'admin') {
          query = query.eq('client_id', user.id); // Sécurité : seul le propriétaire
        }

        const { data, error } = await query.single();

        if (error) {
          console.error('Erreur chargement site :', error);
          setErreur('Impossible de charger ce site. Vérifiez que vous en êtes bien le propriétaire.');
          setChargement(false);
          return;
        }

        if (!data) {
          setErreur('Site introuvable');
          setChargement(false);
          return;
        }

        setSite(data);
      } catch (err) {
        console.error('Exception :', err);
        setErreur('Erreur inattendue : ' + err.message);
      } finally {
        setChargement(false);
      }
    };

    chargerSite();
  }, [siteId, user]);

  // ─────────────────────────────────────────────────────────────
  // Scroll auto vers section paiement si ?scroll=paiement
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!site || chargement) return;

    const doitScroller = searchParams.get('scroll') === 'paiement';
    if (!doitScroller) return;

    // Attendre que PreviewSite ait fini de rendre
    setTimeout(() => {
      const sectionPaiement = document.querySelector('.preview-paiement');
      if (sectionPaiement) {
        sectionPaiement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 800);
  }, [site, chargement, searchParams]);

  // ─────────────────────────────────────────────────────────────
  // Handler retour vers tableau de bord
  // ─────────────────────────────────────────────────────────────
  const retourTableauDeBord = () => {
    navigate('/tableau-de-bord');
  };

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Chargement
  // ─────────────────────────────────────────────────────────────
  if (chargement) {
    return (
      <div className="voir-site-loading">
        <div className="voir-site-spinner"></div>
        <p>Chargement de votre site...</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Erreur
  // ─────────────────────────────────────────────────────────────
  if (erreur) {
    return (
      <div className="voir-site-erreur">
        <div className="voir-site-erreur-icone">⚠️</div>
        <h2>Une erreur est survenue</h2>
        <p>{erreur}</p>
        <button type="button" className="voir-site-btn-retour" onClick={retourTableauDeBord}>
          ← Retour au tableau de bord
        </button>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Reconstruction des props pour PreviewSite
  // ─────────────────────────────────────────────────────────────
  const contenu = typeof site.contenu_json === 'string'
    ? site.contenu_json
    : JSON.stringify(site.contenu_json);

  const infos = {
    nom: site.nom_organisation,
    ville: site.ville || '',
    email: site.contenu_json?.page_contact?.email || '',
    whatsapp: site.contenu_json?.page_contact?.whatsapp || '',
    adresse: site.contenu_json?.page_contact?.adresse || '',
  };

  const design = site.design_json || {
    couleur1: '#005AA9',
    couleur2: '#00A651',
    couleur3: '#FFFFFF',
    style: 'moderne',
    police: 'poppins',
  };

  const logoUrl = site.photos_json?.logo || '';
  const heroPhotoUrl = site.photos_json?.hero || '';

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Site + Preview
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="voir-site-container" ref={containerRef}>
      {/* Barre supérieure fixe */}
      <div className="voir-site-topbar">
        <button type="button" className="voir-site-btn-back" onClick={retourTableauDeBord}>
          ← Retour au tableau de bord
        </button>
        <div className="voir-site-info">
          <span className="voir-site-nom">{site.nom_organisation}</span>
          <span className={`voir-site-statut voir-site-statut-${site.statut}`}>
            {site.statut === 'brouillon' && '📝 Brouillon'}
            {site.statut === 'paye_en_attente' && '⏳ Paiement en attente'}
            {site.statut === 'publie' && '✅ Publié'}
            {site.statut === 'archive' && '📦 Archivé'}
          </span>
        </div>
      </div>

      {/* Rendu du site via PreviewSite */}
      <PreviewSite
        contenu={contenu}
        infos={infos}
        design={design}
        logoUrl={logoUrl}
        heroPhotoUrl={heroPhotoUrl}
        onRetour={retourTableauDeBord}
        siteId={site.id}
        statut={site.statut}
      />
    </div>
  );
}